import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  Stack,
  Alert,
  Snackbar,
  Autocomplete,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageTitle from "./common/PageTitle";
import { apiService } from "../api/services/apiService";
import { resolveReturnDestination } from "../utils/navigationReturn";
import { useAuth } from "../contexts/AuthContext";
import type { ProductCatalogItem } from "../types";

interface AddSupportCaseProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const AddSupportCase: React.FC<AddSupportCaseProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const createdByEmail = user?.email?.trim() ?? "";

  const goBack = () => {
    if (returnPath) {
      navigate(resolveReturnDestination(returnPath, returnSearch));
      return;
    }
    navigate("/support-cases");
  };

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [products, setProducts] = useState<ProductCatalogItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductCatalogItem | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    apiService
      .getProductCatalog()
      .then(setProducts)
      .catch(() => setProducts([]));
  }, []);

  const handleAdd = async () => {
    if (!name.trim()) {
      setErrorMessage("Title is required");
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.createSupportCase({
        name: name.trim(),
        description: description.trim() || undefined,
        tenantId,
        productUuid: selectedProduct?.uuid,
      });
      setSuccessOpen(true);
      setTimeout(() => goBack(), 800);
    } catch (err: unknown) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to create support case",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageTitle
        pageTitle="New Support Case"
        pageDescription="Submit a support case. It starts in Open status. Linking a product auto-assigns the first product owner."
      />
      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper sx={{ mt: 3, mb: 3, p: 3, maxWidth: 600, border: "1px solid", borderColor: "divider" }}>
          <Stack spacing={3}>
            <TextField
              label="Created by"
              value={createdByEmail}
              fullWidth
              disabled
              helperText="Recorded from your signed-in account."
            />
            <TextField
              label="Title"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={4}
            />
            <Autocomplete
              options={products}
              getOptionLabel={(p) => p.name}
              value={selectedProduct}
              onChange={(_, v) => setSelectedProduct(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Related product (optional)"
                  helperText="When selected, the case is auto-assigned to the first product owner."
                />
              )}
            />
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={goBack} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={submitting || !name.trim()}
              >
                {submitting ? "Submitting..." : "Submit case"}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
      <Snackbar
        open={successOpen}
        autoHideDuration={3000}
        message="Support case created"
        onClose={() => setSuccessOpen(false)}
      />
    </Box>
  );
};

export default AddSupportCase;
